// Pure helpers for the Design My Own composer tools (LAN-716 / LAN-717).
// No DOM, no imports: shared by prompt_composer_tools.js and its node tests,
// and kept in step with languatalk-app/components/speak/promptComposerUtils.ts.

// The Enhance button enables at this many non-whitespace characters. Mirrors
// Stream::PromptEnhancementService::MIN_DRAFT_NON_WHITESPACE_CHARS, which
// rejects shorter drafts server-side.
export const WAND_MIN_NON_WHITESPACE_CHARS = 20;

// Matches the composer textarea maxlength and the server's MAX_DRAFT_CHARS.
export const PROMPT_MAX_CHARS = 20000;

// Must exceed the server's worst case (10 s fast failure + 30 s fallback).
export const ENHANCEMENT_TIMEOUT_MS = 45000;

// A stalled transcription would otherwise leave Enhance / Start chat / Save
// disabled indefinitely. Scribe answers short clips in a few seconds.
export const DICTATION_TIMEOUT_MS = 35000;

// A recording auto-stops (and transcribes what it has) after this long, so an
// abandoned session cannot keep the mic open or grow the upload without bound.
export const MAX_RECORDING_MS = 60000;

export function countNonWhitespace(text) {
  if (!text) return 0;
  return String(text).replace(/\s/g, '').length;
}

export function shouldEnableEnhance(text) {
  return countNonWhitespace(text) >= WAND_MIN_NON_WHITESPACE_CHARS;
}

// Clamp any programmatic write to the field's maxlength: `.val()` bypasses the
// HTML attribute, so dictation and "Use this" must enforce it themselves.
export function clampToMaxChars(text, maxChars = PROMPT_MAX_CHARS) {
  const value = String(text ?? '');
  return value.length > maxChars ? value.slice(0, maxChars) : value;
}

// Append a dictated transcript with the chat composer's rule: one space after
// existing text, direct set when the draft is empty, blank transcripts
// ignored. Never replaces existing text, and never exceeds the field limit.
export function appendTranscript(draft, transcript, maxChars = PROMPT_MAX_CHARS) {
  const text = String(transcript ?? '').trim();
  if (!text) return draft;
  const combined = draft.length > 0 ? `${draft} ${text}` : text;
  return clampToMaxChars(combined, maxChars);
}

// `langua_native_language` values look like "english (american)"; the
// transcription services key their language maps on the plain name. Returns
// '' when unknown so the server falls back to automatic language detection.
export function normaliseLanguageName(name) {
  if (!name) return '';
  return String(name).replace(/\(.*?\)/g, '').trim().toLowerCase();
}

// /langua/stream_speech_to_text answers as text/event-stream:
//   data: {"text":"..."}\n\n
// Concatenate every chunk's text; ignore anything that is not JSON.
export function parseSseTranscript(streamText) {
  let result = '';
  for (const chunk of String(streamText ?? '').split('\n\n')) {
    if (!chunk.startsWith('data: ')) continue;
    try {
      const payload = JSON.parse(chunk.substring('data: '.length));
      if (payload && payload.text) result += payload.text;
    } catch (_) {
      // non-JSON keep-alive or partial chunk
    }
  }
  return result;
}

// Maps an enhancement HTTP failure to the i18n key of the localised copy to
// show. Every code PromptEnhancementEndpoint emits is mapped, so learners see
// their own language; null means "unknown to this client", and the caller
// falls back to the server's English `message`, then the generic copy.
export function enhancementErrorKey(status, code) {
  if (status === 429 || code === 'rate_limited') return 'javascript.chat.enhance_rate_limited';
  if (code === 'draft_too_short') return 'javascript.chat.enhance_too_short';
  if (code === 'draft_too_long') return 'javascript.chat.enhance_too_long';
  if (code === 'enhancement_unavailable' || status >= 500) return 'javascript.chat.enhance_error';
  return null;
}
